import { NgIf } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TelConfirmationComponent } from 'src/app/shared/components/tel-confirmation/tel-confirmation.component';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaInputPhoneComponent } from 'src/app/shared/ui-kit/pa-input-phone/pa-input-phone.component';
import { environment } from 'src/environments/environment';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import { AuthActionsService } from '../../services/auth-actions.service';
import { AuthFlowService } from '../../services/auth-flow.service';
import { AuthSessionStore } from '../../services/auth-session.store';

type TelSubStep = 'enter' | 'confirm';

@Component({
  selector: 'app-tel-verification-step',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    PaButtonComponent,
    PaInputPhoneComponent,
    TelConfirmationComponent,
    SupportLinkV2Component,
  ],
  templateUrl: './tel-verification-step.component.html',
  styleUrls: ['../../styles/_step.styles.scss'],
})
export class TelVerificationStepComponent {
  readonly environment = environment;
  readonly subStep = signal<TelSubStep>('enter');
  readonly authErrorMessage = signal<string | null>(null);

  readonly form = new FormGroup({
    tel: new FormControl('', { nonNullable: true }),
  });

  readonly store = inject(AuthSessionStore);
  private readonly flow = inject(AuthFlowService);
  private readonly actions = inject(AuthActionsService);

  readonly canSkip = computed(
    () => this.store.accountData()?.tel_verify?.required === false,
  );

  constructor() {
    this.form.controls.tel.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.authErrorMessage.set(null));
  }

  back(): void {
    if (this.subStep() === 'confirm') {
      this.subStep.set('enter');
      return;
    }

    this.flow.goBack();
  }

  isTelEmpty(): boolean {
    return !this.form.controls.tel.value.trim();
  }

  async submitTel(): Promise<void> {
    const control = this.form.controls.tel;
    control.markAsTouched();
    control.updateValueAndValidity();
    if (control.invalid) {
      return;
    }

    this.authErrorMessage.set(null);

    const tel = control.value.replace(/[\s-]/g, '');
    const result = await this.actions.showConfirmWidget(tel);
    if (!result.success) {
      if (result.message) {
        this.authErrorMessage.set(result.message);
      }
      return;
    }

    this.subStep.set('confirm');
  }

  onTelConfirmed(): void {
    this.actions.completeTelVerification();
  }

  skip(): void {
    void this.actions.skipTelConfirmation();
  }

  onWidgetFailed(): void {
    this.actions.confirmWidgetFailedToLoad();
    this.subStep.set('enter');
  }
}
